import {
  Avatar,
  Navbar,
  Image,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenuToggle,
  NavbarMenuItem,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  NavbarMenu,
} from "@nextui-org/react";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import { useTheme as useNextTheme } from "next-themes";
import { Text, Row, SpicySwitch } from "@/components/customComponents";
import { getAvatarUrl } from "@/utils";
import { useEffect, useState } from "react";
import { NavigationLinksConfig, NavigationLinksConfigType } from "@/constants";
import useAuth from "@/services/useAuth";
import TextStyles from "../common/TextStyles.jsx";
import { useApp, useUserInfo } from "@next-platform/client-common-core";
import { ROUTES } from "../../constants";
import { hasSomePermissions } from "@/utils";
import { Icon, IconVariants, IconSize, useMedia } from "@next-platform/client-common-spicy-ui";
import { AvatarPreset } from "@/types";

export const PartnerNavigationBar = () => {
  const { name, avatar_url } = useUserInfo();
  const { branding, applicationName: appName } = useApp();
  const { theme, setTheme } = useNextTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const onThemeChangeHandler = (e) => {
    let colorScheme = e.target.checked ? "dark" : "light";
    setTheme(colorScheme);
  };
  const { isMobile } = useMedia();
  const { isAuthenticated, isLoading, permissions } = useAuth();
  const [logo, setLogo] = useState(branding?.logo);

  const handleClose = () => setIsMenuOpen(false);

  useEffect(() => {
    if (isMobile) {
      setLogo(theme === "dark" ? branding?.logoMobileLight : branding?.logoMobileDark);
    } else {
      setLogo(theme === "dark" ? branding?.logoDesktopLight : branding?.logoDesktopDark);
    }
  }, [isMobile, branding, theme]);

  if (!appName || !branding) return null;
  return (
    <Navbar
      isMenuOpen={isMenuOpen}
      onMenuOpenChange={setIsMenuOpen}
      position={isMenuOpen ? "sticky" : "static"}
      disableAnimation
      isBordered
      className={"partner-navbar navbar mx-auto bg-white dark:bg-black top-0 z-[1000] h-[53.19px] shadow-medium dark:shadow-none"}
    >
      <NavbarBrand>
        <LocalizedLink color="foreground" to={"/"} aria-label={appName}>
          <Image src={logo} alt={""} width={logo ? (isMobile ? 200 : 320) : 0} className="object-cover cursor-pointer" />
          {!isMobile && branding.displayApplicationName && (
            <Text variant={"lg"} b>
              {appName}
            </Text>
          )}
        </LocalizedLink>
      </NavbarBrand>
      <NavbarContent className="max-md:hidden">
        {NavigationLinksConfig.map((linkConfig) => {
          if (hasSomePermissions(permissions, linkConfig.requiredPermissions)) {
            if (linkConfig.type === NavigationLinksConfigType.dropdown) {
              return (
                <Dropdown className="header-dropdown w-[250px]" key={linkConfig.path}>
                  <DropdownTrigger>
                    <NavbarItem className="cursor-pointer flex items-center px-[20px]" key={linkConfig.path}>
                      {linkConfig.label}
                      <Icon icon={IconVariants.CHEVRON_DOWN} size={IconSize.XSMALL} className="ml-1" />
                    </NavbarItem>
                  </DropdownTrigger>
                  <DropdownMenu>
                    {linkConfig.entries.map((entry) => {
                      if (hasSomePermissions(permissions, entry.requiredPermissions)) {
                        return (
                          <DropdownItem key={entry.path}>
                            <LocalizedLink key={entry.path} className="cursor-pointer" color="foreground" to={entry.path} aria-label={entry.label}>
                              {entry.label}
                            </LocalizedLink>
                          </DropdownItem>
                        );
                      }
                    })}
                  </DropdownMenu>
                </Dropdown>
              );
            } else if (linkConfig.type === NavigationLinksConfigType.entry) {
              return (
                <LocalizedLink
                  className="whitespace-nowrap cursor-pointer"
                  color="foreground"
                  key={linkConfig.path || linkConfig.externalLink}
                  to={linkConfig.path || linkConfig.externalLink}
                  aria-label={linkConfig.label}
                >
                  {linkConfig.label}
                </LocalizedLink>
              );
            }
          }
          return null;
        })}
      </NavbarContent>

      <NavbarMenu className={TextStyles.navbar.menu.base}>
        {NavigationLinksConfig.map((linkConfig) => {
          let entries = [linkConfig];
          if (linkConfig.type === NavigationLinksConfigType.dropdown) {
            entries = linkConfig.entries;
          }

          return entries.map((entry) => {
            if (hasSomePermissions(permissions, entry.requiredPermissions)) {
              return (
                <NavbarMenuItem className={TextStyles.navbar.menu.item} onClick={handleClose}>
                  <LocalizedLink
                    className={TextStyles.navbar.menu.link}
                    key={entry.path || entry.externalLink}
                    color="foreground"
                    to={entry.path}
                    aria-label={entry.label}
                  >
                    {entry.label}
                  </LocalizedLink>
                </NavbarMenuItem>
              );
            }
          });
        })}

        {isAuthenticated && (
          <NavbarMenuItem className={TextStyles.navbar.menu.item}>
            <LocalizedLink className={TextStyles.navbar.menu.link} color="foreground" to={ROUTES.profile} aria-label="profile">
              Profile
            </LocalizedLink>
          </NavbarMenuItem>
        )}
        <NavbarMenuItem className={TextStyles.navbar.menu.item}>
          <SpicySwitch variant="primary" defaultSelected={theme == "dark"} onChange={(e) => onThemeChangeHandler(e)} />
        </NavbarMenuItem>
      </NavbarMenu>
      <NavbarContent className="pl-0 gap-0 ml-[5rem]" justify="center">
        <Row justify="end">
          {!isLoading && (
            <>
              {isAuthenticated && (
                <>
                  <NavbarItem className="max-sm:hidden sm:flex items-center ">
                    <LocalizedLink className="cursor-pointer" color="foreground" to={ROUTES.profile} aria-label="profile">
                      {name && (
                        <Avatar
                          className="bg-gray-200"
                          radius="md"
                          name={name}
                          size="md"
                          src={
                            avatar_url
                              ? getAvatarUrl(avatar_url, {
                                  preset: AvatarPreset.AVATAR_48X48,
                                })
                              : null
                          }
                        />
                      )}
                    </LocalizedLink>
                  </NavbarItem>
                </>
              )}
            </>
          )}
          <NavbarItem className="max-md:hidden ml-4 grid place-items-center">
            <SpicySwitch variant="primary" defaultSelected={theme == "dark"} onChange={(e) => onThemeChangeHandler(e)} />
          </NavbarItem>
        </Row>
      </NavbarContent>
      <NavbarContent className="md:hidden max-w-max pl-0">
        <NavbarMenuToggle aria-label="toggle navigation" className="bg-transparent border-none cursor-pointer" />
      </NavbarContent>
    </Navbar>
  );
};
